import React from 'react';
import { Tag, Tooltip } from 'antd';

import { statusHelp, statusLabel } from '../status/statusHelp.js';

/**
 * A status tag that explains itself: the tag a screen already shows, with a
 * tooltip saying what the status means and what to do next
 * (status/statusHelp.js).
 *
 *   <StatusTag kind={STATUS_KIND.TRANSACTION} code={order.state}
 *              color={STATE_COLOUR[order.state]} label={STATE_LABEL[order.state]} />
 *
 * - `kind` + `code` look up the help text, and the label when none is given.
 * - `label` overrides the text on the tag (the screen's own label map, or a
 *   label with an extra part such as "Return due · 12 business days").
 * - `help` overrides the tooltip (a returned cheque's bank reason, say);
 *   when there is no help at all, the plain tag is shown - no empty tooltip.
 * - anything else (style, className) goes to antd's Tag as before.
 *
 * Hover is not enough for everyone: the tag takes keyboard focus (tabIndex 0)
 * so the tooltip opens on focus too, and its aria-label carries the label and
 * the help, so a screen reader hears both - the same pattern FundConfig.jsx
 * uses for its "from <date>" tags.
 */
const StatusTag = ({ kind, code, label, help, color, children, ...tagProps }) => {
    // What the tag says: children win (a tag with markup inside), then an
    // explicit label, then this module's own words for the code.
    const text = children ?? label ?? statusLabel(kind, code);
    const tip = help ?? statusHelp(kind, code);

    if (!tip) {
        return <Tag color={color} {...tagProps}>{text}</Tag>;
    }

    // aria-label needs plain text; when the tag's content is markup, the label
    // (or the code's own label) stands in for it.
    const spoken = typeof text === 'string' ? text : (label ?? statusLabel(kind, code));

    return (
        <Tooltip title={tip}>
            <Tag color={color} tabIndex={0} aria-label={spoken + '. ' + tip} {...tagProps}
                className={['status-tag', tagProps.className].filter(Boolean).join(' ')}>
                {text}
            </Tag>
        </Tooltip>
    );
};

export default StatusTag;
